import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Lead, Task, Opportunity } from '../../types';
import { Sparkles, TrendingUp, AlertTriangle, Lightbulb, ArrowRight } from 'lucide-react';

interface Suggestion {
  id: string;
  type: 'urgent' | 'opportunity' | 'optimization';
  titleKey: string;
  titleParams?: Record<string, string | number>;
  descriptionKey: string;
  descriptionParams?: Record<string, string | number>;
  actionKey: string;
  priority: number;
  target: {
    view: string;
    id?: string;
  };
  entity?: {
    type: 'lead' | 'opportunity' | 'task';
    id: string;
    name: string;
  };
}

interface AIPrioritySuggestionsProps {
  leads: Lead[];
  tasks: Task[];
  opportunities: Opportunity[];
  onNavigate: (view: string, id?: string) => void;
}

const AIPrioritySuggestions: React.FC<AIPrioritySuggestionsProps> = ({
  leads,
  tasks,
  opportunities,
  onNavigate
}) => {
  const { t } = useTranslation('aiSuggestions');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  useEffect(() => {
    generateSuggestions();
  }, [leads, tasks, opportunities]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const generateSuggestions = () => {
    const newSuggestions: Suggestion[] = [];
    const today = new Date();
    const getTime = (value?: string | Date | null) => value ? new Date(value).getTime() : Number.MAX_SAFE_INTEGER;
    const taskTarget = (task: Task) => ({
      target: { view: 'tasks', id: task.id },
      entity: { type: 'task' as const, id: task.id, name: task.title }
    });
    const leadTarget = (lead: Lead) => ({
      target: { view: 'leads', id: lead.id },
      entity: { type: 'lead' as const, id: lead.id, name: `${lead.firstName} ${lead.lastName}`.trim() || lead.company || t('suggestions.genericLead') }
    });
    const opportunityTarget = (opportunity: Opportunity) => ({
      target: { view: 'opportunities', id: opportunity.id },
      entity: { type: 'opportunity' as const, id: opportunity.id, name: opportunity.title }
    });
    const activeOpportunities = opportunities
      .filter(o => o.stage !== 'won' && o.stage !== 'lost')
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    const incompleteLeads = leads
      .filter(l => !l.email || !l.phone || !l.company || !l.assignedTo)
      .sort((a, b) => (b.score || 0) - (a.score || 0));

    // 1. Overdue tasks
    const overdue = tasks
      .filter(t => !t.completed && new Date(t.dueDate) < today)
      .sort((a, b) => getTime(a.dueDate) - getTime(b.dueDate));
    if (overdue.length > 0) {
      newSuggestions.push({
        id: 'overdue-tasks',
        type: 'urgent',
        titleKey: 'suggestions.overdueTasks.title',
        titleParams: { count: overdue.length },
        descriptionKey: 'suggestions.overdueTasks.description',
        actionKey: 'suggestions.overdueTasks.action',
        priority: 10,
        ...taskTarget(overdue[0]),
      });
    }

    // 2. High-score leads without follow-up
    const staleHotLeads = leads
      .filter(l =>
        l.score >= 70 &&
        l.stage !== 'won' &&
        l.stage !== 'lost' &&
        (!l.nextFollowUp || new Date(l.nextFollowUp) < today)
      )
      .sort((a, b) => (b.score || 0) - (a.score || 0) || getTime(a.nextFollowUp) - getTime(b.nextFollowUp));
    if (staleHotLeads.length > 0) {
      const lead = staleHotLeads[0];
      newSuggestions.push({
        id: `stale-lead-${lead.id}`,
        type: 'opportunity',
        titleKey: 'suggestions.hotLeadFollowUp.title',
        descriptionKey: 'suggestions.hotLeadFollowUp.description',
        descriptionParams: { name: `${lead.firstName} ${lead.lastName}`, score: lead.score },
        actionKey: 'suggestions.hotLeadFollowUp.action',
        priority: 9,
        ...leadTarget(lead),
      });
    }

    // 3. Opportunities in closing stage
    const closingOpps = opportunities
      .filter(o => o.stage === 'closing' || o.stage === 'negotiation')
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    if (closingOpps.length > 0) {
      const totalValue = closingOpps.reduce((sum, o) => sum + (o.value || 0), 0);
      newSuggestions.push({
        id: 'closing-opps',
        type: 'opportunity',
        titleKey: 'suggestions.closingDeals.title',
        titleParams: { count: closingOpps.length },
        descriptionKey: 'suggestions.closingDeals.description',
        descriptionParams: { value: formatCurrency(totalValue) },
        actionKey: 'suggestions.closingDeals.action',
        priority: 8,
        ...opportunityTarget(closingOpps[0]),
      });
    }

    // 4. Unassigned high-score leads
    const unassignedHot = leads
      .filter(l => !l.assignedTo && l.score >= 60)
      .sort((a, b) => (b.score || 0) - (a.score || 0));
    if (unassignedHot.length > 0) {
      newSuggestions.push({
        id: 'unassigned-leads',
        type: 'optimization',
        titleKey: 'suggestions.unassignedLeads.title',
        titleParams: { count: unassignedHot.length },
        descriptionKey: 'suggestions.unassignedLeads.description',
        actionKey: 'suggestions.unassignedLeads.action',
        priority: 7,
        ...leadTarget(unassignedHot[0]),
      });
    }

    // 5. Old leads in untouched stage
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const untouchedOld = leads
      .filter(l =>
        l.stage === 'untouched' &&
        new Date(l.createdAt) < weekAgo
      )
      .sort((a, b) => getTime(a.createdAt) - getTime(b.createdAt));
    if (untouchedOld.length > 0) {
      newSuggestions.push({
        id: 'old-untouched',
        type: 'optimization',
        titleKey: 'suggestions.oldUntouched.title',
        titleParams: { count: untouchedOld.length },
        descriptionKey: 'suggestions.oldUntouched.description',
        actionKey: 'suggestions.oldUntouched.action',
        priority: 6,
        ...leadTarget(untouchedOld[0]),
      });
    }

    // 6. Opportunities needing follow-up
    const needsFollowUp = opportunities
      .filter(o =>
        o.stage !== 'won' &&
        o.stage !== 'lost' &&
        o.nextFollowUp &&
        new Date(o.nextFollowUp) < today
      )
      .sort((a, b) => getTime(a.nextFollowUp) - getTime(b.nextFollowUp));
    if (needsFollowUp.length > 0) {
      newSuggestions.push({
        id: 'follow-up-opps',
        type: 'urgent',
        titleKey: 'suggestions.followUpOpps.title',
        titleParams: { count: needsFollowUp.length },
        descriptionKey: 'suggestions.followUpOpps.description',
        actionKey: 'suggestions.followUpOpps.action',
        priority: 8,
        ...opportunityTarget(needsFollowUp[0]),
      });
    }

    // 7. Leads in demo stage
    const demoStage = leads
      .filter(l => l.stage === 'demo')
      .sort((a, b) => (b.score || 0) - (a.score || 0));
    if (demoStage.length > 0) {
      newSuggestions.push({
        id: 'demo-leads',
        type: 'opportunity',
        titleKey: 'suggestions.demoLeads.title',
        titleParams: { count: demoStage.length },
        descriptionKey: 'suggestions.demoLeads.description',
        actionKey: 'suggestions.demoLeads.action',
        priority: 7,
        ...leadTarget(demoStage[0]),
      });
    }

    // 8. Tasks due today
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    const dueToday = tasks
      .filter(t =>
        !t.completed &&
        new Date(t.dueDate) >= today &&
        new Date(t.dueDate) <= todayEnd
      )
      .sort((a, b) => getTime(a.dueDate) - getTime(b.dueDate));
    if (dueToday.length > 0) {
      newSuggestions.push({
        id: 'due-today',
        type: 'urgent',
        titleKey: 'suggestions.dueToday.title',
        titleParams: { count: dueToday.length },
        descriptionKey: 'suggestions.dueToday.description',
        actionKey: 'suggestions.dueToday.action',
        priority: 9,
        ...taskTarget(dueToday[0]),
      });
    }

    // 9. High-value opportunities
    const highValueOpps = opportunities
      .filter(o =>
        o.stage !== 'won' &&
        o.stage !== 'lost' &&
        (o.value || 0) > 10000
      )
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    if (highValueOpps.length > 0) {
      const totalValue = highValueOpps.reduce((sum, o) => sum + (o.value || 0), 0);
      newSuggestions.push({
        id: 'high-value',
        type: 'opportunity',
        titleKey: 'suggestions.highValue.title',
        titleParams: { count: highValueOpps.length },
        descriptionKey: 'suggestions.highValue.description',
        descriptionParams: { value: formatCurrency(totalValue) },
        actionKey: 'suggestions.highValue.action',
        priority: 8,
        ...opportunityTarget(highValueOpps[0]),
      });
    }

    // 10. Recent leads needing attention
    const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000);
    const recentLeads = leads
      .filter(l =>
        new Date(l.createdAt) >= threeDaysAgo &&
        l.stage === 'untouched'
      )
      .sort((a, b) => getTime(a.createdAt) - getTime(b.createdAt));
    if (recentLeads.length > 0) {
      newSuggestions.push({
        id: 'recent-untouched',
        type: 'optimization',
        titleKey: 'suggestions.recentUntouched.title',
        titleParams: { count: recentLeads.length },
        descriptionKey: 'suggestions.recentUntouched.description',
        actionKey: 'suggestions.recentUntouched.action',
        priority: 7,
        ...leadTarget(recentLeads[0]),
      });
    }

    // Add generic suggestions if we don't have enough
    if (newSuggestions.length < 5) {
      // General optimization suggestions
      if (newSuggestions.length < 5 && leads.length > 0) {
        const avgScore = leads.reduce((sum, l) => sum + l.score, 0) / leads.length;
        const focusLead = [...leads].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
        newSuggestions.push({
          id: 'lead-quality',
          type: 'optimization',
          titleKey: 'suggestions.leadQuality.title',
          descriptionKey: 'suggestions.leadQuality.description',
          descriptionParams: { score: avgScore.toFixed(0) },
          actionKey: 'suggestions.leadQuality.action',
          priority: 5,
          ...leadTarget(focusLead),
        });
      }

      if (newSuggestions.length < 5 && opportunities.length > 0) {
        const conversionRate = opportunities.filter(o => o.stage === 'won').length / opportunities.length * 100;
        newSuggestions.push({
          id: 'conversion',
          type: 'optimization',
          titleKey: 'suggestions.conversionRate.title',
          descriptionKey: 'suggestions.conversionRate.description',
          descriptionParams: { rate: conversionRate.toFixed(1) },
          actionKey: 'suggestions.conversionRate.action',
          priority: 5,
          target: { view: 'insights' },
        });
      }

      if (newSuggestions.length < 5) {
        const pipelineFocus = activeOpportunities[0];
        newSuggestions.push({
          id: 'pipeline-review',
          type: 'optimization',
          titleKey: 'suggestions.pipelineReview.title',
          descriptionKey: 'suggestions.pipelineReview.description',
          actionKey: 'suggestions.pipelineReview.action',
          priority: 4,
          ...(pipelineFocus ? opportunityTarget(pipelineFocus) : { target: { view: 'opportunities' } }),
        });
      }

      if (newSuggestions.length < 5) {
        newSuggestions.push({
          id: 'team-performance',
          type: 'optimization',
          titleKey: 'suggestions.teamPerformance.title',
          descriptionKey: 'suggestions.teamPerformance.description',
          actionKey: 'suggestions.teamPerformance.action',
          priority: 4,
          target: { view: 'dashboard' },
        });
      }

      if (newSuggestions.length < 5) {
        const dataQualityLead = incompleteLeads[0];
        newSuggestions.push({
          id: 'data-quality',
          type: 'optimization',
          titleKey: 'suggestions.dataQuality.title',
          descriptionKey: 'suggestions.dataQuality.description',
          actionKey: 'suggestions.dataQuality.action',
          priority: 3,
          ...(dataQualityLead ? leadTarget(dataQualityLead) : { target: { view: 'leads' } }),
        });
      }
    }

    // Sort by priority and take top 5
    newSuggestions.sort((a, b) => b.priority - a.priority);
    setSuggestions(newSuggestions.slice(0, 5));
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'urgent': return <AlertTriangle className="w-5 h-5 text-danger" />;
      case 'opportunity': return <TrendingUp className="w-5 h-5 text-accent" />;
      case 'optimization': return <Lightbulb className="w-5 h-5 text-txt-secondary" />;
      default: return <Sparkles className="w-5 h-5 text-txt-secondary" />;
    }
  };

  const getColor = (type: string) => {
    switch (type) {
      case 'urgent': return { bg: 'bg-danger-soft', border: 'border-danger', text: 'text-danger', badge: 'bg-danger-soft text-danger' };
      case 'opportunity': return { bg: 'bg-bg-card', border: 'border-default', text: 'text-txt', badge: 'bg-info-soft text-accent' };
      case 'optimization': return { bg: 'bg-bg-card', border: 'border-default', text: 'text-txt', badge: 'bg-bg-surface text-txt-secondary' };
      default: return { bg: 'bg-bg-card', border: 'border-default', text: 'text-txt', badge: 'bg-bg-surface text-txt-secondary' };
    }
  };

  return (
    <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 h-full flex flex-col p-4 sm:p-6 hover:shadow-card transition-shadow duration-200">
      <div className="flex items-center space-x-2 mb-4 flex-shrink-0">
        <div className="p-2 bg-accent-soft rounded-lg">
          <Sparkles className="w-5 h-5 text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-txt">{t('title')}</h3>
          <p className="text-sm text-txt-secondary">{t('subtitle')}</p>
        </div>
      </div>

      <div className="space-y-3 flex-1 overflow-y-auto">
        {suggestions.length === 0 ? (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-txt-muted mx-auto mb-3" />
            <p className="text-txt-tertiary text-sm">{t('loading')}</p>
            <p className="text-xs text-txt-muted mt-1">{t('pleaseWait')}</p>
          </div>
        ) : (
          suggestions.map((suggestion) => {
            const colors = getColor(suggestion.type);
            return (
            <div
              key={suggestion.id}
              className={`p-4 ${colors.bg} border ${colors.border} rounded-xl hover:shadow-card transition-all cursor-pointer group`}
              onClick={() => {
                onNavigate(suggestion.target.view, suggestion.target.id);
              }}
            >
              <div className="flex items-start space-x-3">
                {getIcon(suggestion.type)}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={`font-semibold ${colors.text}`}>
                      {t(suggestion.titleKey, suggestion.titleParams)}
                    </h4>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${colors.badge}`}>
                      {t('priority')} {suggestion.priority}
                    </span>
                  </div>
                  <p className="text-sm text-txt-secondary mb-2">
                    {t(suggestion.descriptionKey, suggestion.descriptionParams)}
                  </p>
                  {suggestion.entity && (
                    <p className="mb-2 truncate text-xs font-medium text-txt-tertiary">
                      {t('opens')} {suggestion.entity.name}
                    </p>
                  )}
                  <div className="flex items-center space-x-2 text-sm font-medium text-txt group-hover:text-accent transition-colors">
                    <span>{t(suggestion.actionKey)}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AIPrioritySuggestions;
